import classnames from 'classnames';

import {
  InspectorControlsAppearance,
  InspectorControlsGeneral,
  InspectorTabs,
} from '@components/inspector-tabs';
import Visibility from '@components/visibility';
import ClassDropdown, { getBlockClassNames } from '@components/class-dropdown';
import Label from '@components/block-label';
import { DEFAULT_CSS_CLASS } from '@constants';
import { getPosts } from '@helpers/post-block-utils';
import { store as coreStore } from '@wordpress/core-data';
import apiFetch from '@wordpress/api-fetch';
import parse from 'html-react-parser';
import { startCase } from 'lodash';
import { cacheMiddleware } from '@components/api-call-cache-middleware';

import './editor.scss';

const {
  i18n: { __ },
  components: { SelectControl, PanelBody, Notice },
  element: { useEffect, useState },
  date: { dateI18n },
  blockEditor: { useBlockProps },
  hooks: { applyFilters },
  data: { withSelect },
} = wp;

const InspectorAppearance = ({ attributes, setAttributes }) => {
  const { blockClassNames } = attributes;
  const defaultBlockCssClass = DEFAULT_CSS_CLASS.META_FIELD;
  return (
    <InspectorControlsAppearance>
      <ClassDropdown
        {...{
          blockClassNames,
          setAttributes,
          blockClassPrefix: defaultBlockCssClass,
        }}
      />
    </InspectorControlsAppearance>
  );
};

const InspectorGeneral = ({
  attributes,
  setAttributes,
  metaFieldsOptions,
  metaFieldOutput,
}) => {
  const { blockVisibility, selectedMetaField, label } = attributes;

  const setMetaFieldLabel = (metaField) => {
    let metaFieldLabel = '';
    const customMetaField =
      metaField.toLowerCase().indexOf('taxonomy_') >= 0
        ? metaField.replace('taxonomy_', '')
        : metaField;

    switch (customMetaField) {
      case 'learn_more_label':
        metaFieldLabel = 'Read more';
        break;
      case 'select_meta':
        metaFieldLabel = 'Meta Field';
        break;
      default:
        metaFieldLabel = `${customMetaField
          .charAt(0)
          .toUpperCase()}${customMetaField.slice(1).replace(/_/g, ' ')}`;
    }
    return metaFieldLabel;
  };

  const onChangeMetaField = (metaField) => {
    const selectedFieldOutput = metaFieldOutput(metaField);

    setAttributes({
      selectedMetaField: metaField,
      selectedFieldOutput,
      label: setMetaFieldLabel(metaField),
    });
  };

  return (
    <InspectorControlsGeneral>
      <Label {...{ label, setAttributes }} />
      <Visibility {...{ blockVisibility, setAttributes }} />
      <PanelBody title={__('Select Meta Field', 'b3rg')}>
        <SelectControl
          options={metaFieldsOptions}
          value={selectedMetaField}
          onChange={onChangeMetaField}
        />
      </PanelBody>
    </InspectorControlsGeneral>
  );
};

const Edit = (props) => {
  const { attributes, setAttributes, context, clientId, taxonomies } = props;
  const { blockVisibility, blockClassNames, selectedMetaField } = attributes;

  const { postId, postType, dateFormat, anchorAppearance, fancyBoxClasses } =
    context;

  const [metaFieldsOptions, setMetaFieldsOptions] = useState([]);
  const [metaFieldsData, setMetaFieldsData] = useState([]);
  const [selectedFieldOutput, setSelectedFieldOutput] = useState('');

  useEffect(() => {
    const query = {
      status: 'publish',
      include: postId,
    };
    const updatedQuery = applyFilters('b3rg_meta_field_query', query);
    getPosts(postType, setMetaData, updatedQuery);
    setAttributes({
      selectedDateFormat: dateFormat,
      anchorAppearance,
      fancyBoxClasses,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedMetaField,
    dateFormat,
    anchorAppearance,
    fancyBoxClasses,
    taxonomies,
    postId,
  ]);

  const setMetaData = (currentPost) => {
    if (currentPost.length) {
      const { meta, date: publishDate } = currentPost[0];
      let metaFields = Object.getOwnPropertyNames(meta);
      //insert new meta field from theme side
      metaFields = applyFilters('b3rg_meta_field_include_fields', metaFields);

      //add post date
      metaFields = [...metaFields, 'post_date', 'author_details'];

      let excludeMetaFields = [
        'show_custom_date',
        'featured',
        'featured_image',
        'image_alt_text',
        'learn_more_type',
        'learn_more_link',
        'show_popup',
        'learn_more_link_file',
        'event_date',
        'featured_page_list',
        'footnotes',
        'place_holder_image_url',
        'selected_taxonomy_terms',
      ];

      //filter for override excludeMetaField list from theme
      excludeMetaFields = applyFilters(
        'b3rg_meta_field_exclude_fields',
        excludeMetaFields,
      );

      const metaOptions = [
        { value: 'select_meta', label: 'Select Meta Field' },
      ];
      metaFields.forEach((metaField) => {
        const labelField =
          metaField === 'learn_more_label' ? 'read_more' : metaField;
        if (!excludeMetaFields.includes(metaField)) {
          const meteOption = {
            value: metaField,
            label:
              labelField.charAt(0).toUpperCase() +
              labelField.slice(1).replace(/_/g, ' '),
          };
          metaOptions.push(meteOption);
        }
      });

      const eventDates = ['event_start_date', 'event_end_date'];
      const postHasEventDates = eventDates.every((item) => {
        return metaFields.includes(item);
      });

      if (postHasEventDates) {
        metaOptions.push({
          value: 'event_start_end',
          label: 'Event Date [Start Date - End Date]',
        });
      }

      if (taxonomies?.length) {
        taxonomies.forEach((taxonomy) => {
          const {
            name,
            slug,
            visibility: { public: publicVisibility },
          } = taxonomy;
          if (publicVisibility) {
            metaOptions.push({ value: `taxonomy_${slug}`, label: name });
          }
        });
      }

      const primaryTermTaxonomies = applyFilters(
        'b3rg_primary_term_enabled_taxonomies',
        [],
      );

      const primaryTermTaxonomiesForPostType =
        primaryTermTaxonomies[postType] || [];

      if (primaryTermTaxonomiesForPostType?.length) {
        primaryTermTaxonomiesForPostType.forEach((primaryTaxonomy) => {
          metaOptions.push({
            value: `primary_${primaryTaxonomy}`,
            label: 'Primary Term - ' + startCase(primaryTaxonomy),
          });
        });
      }

      setMetaFieldsOptions(metaOptions);
      setMetaFieldsData({ ...meta, publishDate });
      setSelectedFieldOutput(
        metaFieldOutput(selectedMetaField, { ...meta, publishDate }),
      );
    }
  };

  const defaultOutput = (
    <Notice status="info" isDismissible={false}>
      {__('Selected field rendered empty.', 'b3rg')}
    </Notice>
  );

  const metaFieldOutput = (metaField, metaData = '') => {
    const customMetaData = metaData ? metaData : metaFieldsData;

    let customMetaField = '';
    if (metaField.toLowerCase().indexOf('taxonomy_') >= 0) {
      customMetaField = 'taxonomy_field';
    } else if (metaField.toLowerCase().indexOf('primary_') >= 0) {
      customMetaField = 'primary_field';
    } else {
      customMetaField = metaField;
    }

    switch (customMetaField) {
      case 'custom_date':
      case 'event_end_date':
      case 'event_start_date':
        return customMetaData[customMetaField]
          ? dateI18n(dateFormat || 'M j, Y', customMetaData[customMetaField])
          : defaultOutput;
      case 'learn_more_label':
        const readMoreData = {
          metaField,
          anchorAppearance,
          postId,
          fancyBoxClasses,
        };
        return setMetaFieldOutPut(readMoreData);

      case 'event_start_end':
        return customMetaData.event_start_date &&
          customMetaData.event_end_date ? (
          <>
            {dateI18n(dateFormat || 'M j, Y', customMetaData.event_start_date)}
            {' - '}
            {dateI18n(dateFormat || 'M j, Y', customMetaData.event_end_date)}
          </>
        ) : (
          defaultOutput
        );
      case 'taxonomy_field':
        const taxonomyData = {
          taxonomyName: metaField.replace('taxonomy_', ''),
          anchorAppearance,
          postId,
          metaField,
        };
        setMetaFieldOutPut(taxonomyData);
        break;
      case 'primary_field':
        setMetaFieldOutPut({
          taxonomyName: metaField.replace('primary_', ''),
          postId,
          metaField,
          anchorAppearance,
        });
        break;
      case 'post_date':
        return dateI18n(dateFormat || 'M j, Y', customMetaData.publishDate);
      case 'post_reading_time':
        const postReadingData = {
          postId,
          metaField,
        };
        setMetaFieldOutPut(postReadingData);
        break;
      case 'author_details':
        const authorData = {
          postId,
          metaField,
        };
        setMetaFieldOutPut(authorData);
        break;
      case 'select_meta':
        return (
          <Notice status="info" isDismissible={false}>
            {__('Please select meta field.', 'b3rg')}
          </Notice>
        );
      default:
        const defaultParams = {
          postId,
          attributes,
          metaField,
        };
        setMetaFieldOutPut(defaultParams);
    }
  };

  const setMetaFieldOutPut = (params) => {
    apiFetch.use(cacheMiddleware);
    apiFetch({
      path: '/api/b3rg/meta-field/setOutput',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
      cachingEnabled: true,
    })
      .then((outputHtml) => {
        setSelectedFieldOutput(outputHtml ? parse(outputHtml) : defaultOutput);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e);
      });
  };

  const defaultBlockCssClass = DEFAULT_CSS_CLASS.META_FIELD;

  const metaFieldClasses = getBlockClassNames(blockClassNames);
  const selectedMetaFieldClass = selectedMetaField.replaceAll('_', '-');
  const blockProps = useBlockProps({
    className: classnames({
      [`${defaultBlockCssClass}--taxonomy`]:
        selectedMetaField.indexOf('taxonomy_') === 0,
      [` ${defaultBlockCssClass} ${defaultBlockCssClass}--${selectedMetaFieldClass} ${defaultBlockCssClass}_${clientId} ${metaFieldClasses}`]: true,
      disable: !blockVisibility,
    }),
  });

  const property = {
    ...props,
    metaFieldsOptions,
    metaFieldOutput,
  };

  return (
    <>
      <InspectorTabs>
        <InspectorGeneral {...property} />
        <InspectorAppearance {...props} />
      </InspectorTabs>
      <div {...blockProps}>{selectedFieldOutput}</div>
    </>
  );
};

export default withSelect((select, props) => {
  const {
    context: { postType },
  } = props;

  const taxonomies =
    postType === 'page'
      ? ''
      : select(coreStore).getTaxonomies({
          per_page: -1,
          type: postType,
        });

  return { taxonomies };
})(Edit);
